import { MainframeService } from '@amzn/awscat-portfolio-assessment-service-client';
import { StoredEnvironment } from '@amzn/awscat-portfolio-assessment-types';
import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import { Box, Button, Container, FormField, Header, Input, SpaceBetween, Spinner } from '@amzn/awsui-components-react';
import { useEffect, useState } from 'react';
import { match, useHistory, useRouteMatch } from 'react-router';
import { useIsMounted } from 'react-tidy';

import { constants } from '../../../common/Localization';
import { MatchParam } from '../../../common/types';
import { deepCopy } from '../../../common/util';
import ConfirmationModal from '../../common/ConfirmationModal';
import MainframeEnvironment from './MainframeEnvironment';

type Props = FlashContextInterface;
type MainframeToSave = {
    name: string;
    id: string;
    make: string;
}

const mainframeService = new MainframeService();

function CreateOrEditMainframe({ flash }: Props): JSX.Element {
    const [ loading, setLoading ] = useState(false);
    const [ validEnvs, setValidEnvs ] = useState<boolean[]>([]);
    const history = useHistory();
    const isMounted = useIsMounted();
    const [ mainframeToSave, setMainframeToSave ] = useState<MainframeToSave>({
        name: '',
        id: '',
        make: '',
    });
    const [ initialEnvs, setInitialEnvs ] = useState<StoredEnvironment[]>([]);
    const [ modifiedEnvs, setModifiedEnvs ] = useState<StoredEnvironment[]>([]);
    const [ cancelFrictionVisible, setCancelFrictionVisible ] = useState(false);
    const portfolioMatch: match<MatchParam> | null = useRouteMatch({
        path: '/portfolio/:portfolioId/',
    });
    const editMatch: match<MatchParam> | null = useRouteMatch({
        path: '/portfolio/:portfolioId/management/assets/mainframe/edit/:mainframeId',
    });
    const portfolioId = portfolioMatch?.params?.portfolioId || '';
    const mainframeId = editMatch?.params?.mainframeId || '';
    useEffect(() => {
        const loadExistingMainframe = async () => {
            try {
                setLoading(true);
                const result = await mainframeService.getById(portfolioId, mainframeId);
                if (isMounted()) {
                    setMainframeToSave({
                        name: result.name,
                        make: result.make,
                        id: result.id,
                    });
                    setInitialEnvs(result.environments);
                    setModifiedEnvs(deepCopy(result.environments));
                    setValidEnvs(new Array(result.environments.length).fill(true));
                }
            } catch (err) {
                if (isMounted()) {
                    flash.alert(FlashType.error, (err as Error).message, constants.FAILED_TO_LOAD_EXISTING_MAINFRAME);
                }
            } finally {
                if (isMounted()) {
                    setLoading(false);
                }
            }
        };
        if (mainframeId) {
            // Case Edit
            loadExistingMainframe();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    if (loading) {
        return <Spinner size='large'></Spinner>;
    }
    function getProdEnv() {
        const prodEnv = modifiedEnvs.filter(env => env.envType === 'prod');
        return prodEnv.length == 1 ? prodEnv[0] : null;
    };
    function doCalcEnvDerivedValues(prodEnv: StoredEnvironment, nonProdEnv: StoredEnvironment) {
        // 10% of prod
        nonProdEnv.avgOltpMips = prodEnv.avgOltpMips ? prodEnv.avgOltpMips * 0.10 : 0;
        // 10% of prod
        nonProdEnv.avgBatchMips = prodEnv.avgBatchMips ? prodEnv.avgBatchMips * 0.10 : 0;
        // 10% of prod
        nonProdEnv.avgDBMips = prodEnv.avgDBMips ? prodEnv.avgDBMips * 0.10 : 0;
        // 5% of prod
        nonProdEnv.dbDiskUsedTB = prodEnv.dbDiskUsedTB ? prodEnv.dbDiskUsedTB * 0.05 : 0;
        // 5% of prod
        nonProdEnv.filesDiskUsedTB = prodEnv.filesDiskUsedTB ? prodEnv.filesDiskUsedTB * 0.05 : 0;
        // 0.1% of prod
        nonProdEnv.dataTransferOutGB = prodEnv.dataTransferOutGB ? prodEnv.dataTransferOutGB * 0.001 : 0;
        // same appEnvCount and dbEnvCount as prod
        nonProdEnv.appEnvCount = prodEnv.appEnvCount;
        nonProdEnv.dbEnvCount = prodEnv.dbEnvCount;
    }
    const onAddEnvironment = () => {
        const newEnv: StoredEnvironment = {
            oltpHrs: 168,
            batchHrs: 168,
        } as StoredEnvironment;

        const prodEnv = getProdEnv();
        // Create the one prod env if it hasn't been created yet.
        if (!prodEnv) {
            newEnv.envType = 'prod';
        }
        // Else the new env is non-prod, derive some values from prod that will be read-only in the new env.
        else {
            doCalcEnvDerivedValues(prodEnv, newEnv);
        }
        const envs = initialEnvs;
        setInitialEnvs([...envs, newEnv]);
        setModifiedEnvs([...modifiedEnvs, newEnv]);
        setValidEnvs([...validEnvs, false]);
    };
    const setEnvironment = (index: number, newEnv: StoredEnvironment) => {
        modifiedEnvs.splice(index, 1, newEnv);
        if (newEnv.envType === 'prod') {
            // derive other environments
            modifiedEnvs
                .filter(env => env.envType !== 'prod')
                .forEach(nonProdEnv => doCalcEnvDerivedValues(newEnv, nonProdEnv));
        }
        setModifiedEnvs(modifiedEnvs);
    };
    const setEnvValidity = (envIndex: number, isValid: boolean) => {
        const validEnvsCopy = [...validEnvs];
        validEnvsCopy[envIndex] = isValid;
        setValidEnvs(validEnvsCopy);
    };
    const onDeleteEnv = (index: number) => {
        const initialEnvsCopy = [...initialEnvs];
        initialEnvsCopy.splice(index, 1);
        setInitialEnvs(initialEnvsCopy);

        const modifiedEnvsCopy = [...modifiedEnvs];
        modifiedEnvsCopy.splice(index, 1);
        setModifiedEnvs(modifiedEnvsCopy);
        
        const validEnvsCopy = [...validEnvs];
        validEnvsCopy.splice(index, 1);
        setValidEnvs(validEnvsCopy);
    };
    const environmentSections = initialEnvs.map((environment, index) => 
        <MainframeEnvironment
            key={index}
            environment={environment}
            environmentIndex={index}
            setEnvValidity={setEnvValidity}
            setEnvironment={setEnvironment}
            deleteEnvironment={onDeleteEnv} />);
    const redirectToMainframePage = () => {
        history.push(`/portfolio/${portfolioId}/management/assets/mainframe`);
    };
    const onSave = async () => {
        try {
            setLoading(true);
            const mainframeBody = {
                ...mainframeToSave,
                environments: modifiedEnvs,
            };
            if (mainframeId) {
                // Case edit
                await mainframeService.save(portfolioId, mainframeId, { mainframe: mainframeBody });
            } else {
                // Case create
                await mainframeService.create(portfolioId, { mainframe: mainframeBody });
            }
            if (isMounted()) {
                redirectToMainframePage();
            }
        } catch (err) {
            if (isMounted()) {
                flash.alert(FlashType.error, (err as Error).message, constants.FAILED_TO_SAVE_MAINFRAME);
            }
        } finally {
            if (isMounted()) {
                setLoading(false);
            }
        }
    };
    const onCancel = () => {
        setCancelFrictionVisible(true);
    };

    let environments = (
        <Container>
            <SpaceBetween size='m'>
                <Box textAlign='center'>{constants.YOU_HAVE_NOT_YET_ENVS}</Box>
                <Box textAlign='center'>
                    <Button variant='primary' onClick={onAddEnvironment}>{constants.ADD_ENVIRONMENT}</Button>
                </Box>
            </SpaceBetween>
        </Container>
    );
    if (initialEnvs.length > 0) {
        environments = (
            <Container header={
                <Header variant='h2' actions={<Button onClick={onAddEnvironment}>{constants.ADD_ENVIRONMENT}</Button>}>
                    {constants.ENVIRONMENTS}
                </Header>}>
                <SpaceBetween size='l'>
                    {environmentSections}
                </SpaceBetween>
            </Container>
        );
    }
    
    return (
        <SpaceBetween size='l'>
            <Header variant='h1'>{mainframeId ? constants.EDIT_BUTTON_TEXT : constants.CREATE_BUTTON_TEXT}{constants.MAINFRAME_WORKLOAD}</Header>
            <Container header={constants.MAINFRAME_WORKLOAD_CONFIG}>
                <FormField
                    label={constants.MAINFRAME_WORKLOAD_NAME}
                    description={constants.CREATE_A_SHORT_MAINFRAME_NAME}>
                    <Input value={mainframeToSave.name} onChange={e => setMainframeToSave({...mainframeToSave, name: e.detail.value})}></Input>
                </FormField>
            </Container>
            {environments}
            <Box float='right'>
                <SpaceBetween size='m' direction='horizontal'>
                    <Button variant='link' onClick={onCancel}>{constants.CANCEL_BUTTON_TEXT}</Button>
                    <Button variant='primary' onClick={onSave} disabled={
                        validEnvs.some(isValid => !isValid) || !mainframeToSave.name || !modifiedEnvs.length}>{constants.SAVE_BUTTON_TEXT}</Button>
                </SpaceBetween>
            </Box>
            { cancelFrictionVisible ? (
                <ConfirmationModal
                    confirmButton={constants.CONTINUE_BUTTON_TEXT}
                    confirmLoading={loading}
                    onConfirm={redirectToMainframePage}
                    onDismiss={() => { setCancelFrictionVisible(false); }}
                    header={constants.UNSAVED_CHANGES_WILL_BE_LOST}
                    visible
                >
                    {constants.UNSAVED_CHANGES_WILL_BE_LOST_CONTINUE}
                </ConfirmationModal>
            ) : null }
        </SpaceBetween>
    );
}

export default withFlashContext(CreateOrEditMainframe);
